import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { PARTNER_PORTAL_URL } from '../../lib/partnerPortal';

/**
 * Public navigation.
 *
 * Preserved from the measured baseline (§1.5, P-5/P-6):
 *   - sticky, white/90 translucent, 1px bottom border #f3f5f6
 *   - logo mark 48x48 + wordmark 96x32
 *   - links 14px/600; active #0b2d4d, inactive charcoal/80
 *   - "Request Quote" primary CTA at 10px 24px
 *
 * Current link order, left to right:
 *   Home / About / For Schools / Catalogue / Contact / Become a Partner /
 *   [Request Quote] / [Login/Register]
 *
 * "Become a Partner" and "Login/Register" are DELIBERATE ADDITIONS for the
 * affiliate/partner programme launch (MS-V2-AFF-001) — not baseline drift from
 * the original five-link measurement above.
 *   - "Become a Partner" is a sixth NAV_LINKS entry, so it renders with the
 *     exact same styling and aria-current handling as the other five, and
 *     routes to the new in-app /partners page.
 *   - "Login/Register" is an anchor styled as a button (btn-navy, visually
 *     secondary) next to the btn-primary Request Quote CTA so the quote CTA
 *     keeps primacy. It is a real cross-origin <a>, not a wouter <Link> — the
 *     partner portal is a separate application on its own host (see
 *     lib/partnerPortal.ts and the "NO ADMIN ROUTES" rule in App.tsx).
 *
 * Refined (R-5): the mobile menu is a real disclosure — aria-expanded, Escape to
 * close, focus returned to the trigger. Production's appearance is unchanged.
 */

const NAV_LINKS = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/for-schools', label: 'For Schools' },
  { href: '/catalogue', label: 'Catalogue' },
  { href: '/contact', label: 'Contact' },
  { href: '/partners', label: 'Become a Partner' },
] as const;

export function Navbar() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on route change.
  useEffect(() => setOpen(false), [location]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const linkClass = (href: string) =>
    `text-sm font-semibold transition-colors ${
      location === href ? 'text-navy' : 'text-charcoal/80 hover:text-navy'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/90 backdrop-blur">
      <nav
        className="container-content flex h-16 items-center justify-between"
        aria-label="Main"
      >
        <Link href="/" className="flex items-center gap-3" aria-label="Mopani Suppliers — home">
          <img
            src="/assets/logo-mark.png"
            alt=""
            width={48}
            height={48}
            className="h-12 w-12 object-contain"
          />
          <img
            src="/assets/wordmark.png"
            alt="Mopani Suppliers"
            width={96}
            height={32}
            className="h-8 w-24 object-contain"
          />
        </Link>

        {/* Desktop */}
        <div className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className={linkClass(l.href)}
              aria-current={location === l.href ? 'page' : undefined}
            >
              {l.label}
            </Link>
          ))}
          <Link href="/quote" className="btn btn-primary btn-nav">
            Request Quote
          </Link>
          {/* Real cross-origin link (not a wouter <Link>) — see the file header. */}
          <a href={PARTNER_PORTAL_URL} className="btn btn-navy btn-nav">
            Login/Register
          </a>
        </div>

        {/* Mobile trigger */}
        <button
          ref={triggerRef}
          type="button"
          className="md:hidden rounded p-2 text-navy"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {open ? (
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </nav>

      {open && (
        <div id="mobile-menu" className="border-t border-border bg-white md:hidden">
          <div className="container-content flex flex-col gap-1 py-4">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className={`${linkClass(l.href)} py-3`}
                aria-current={location === l.href ? 'page' : undefined}
              >
                {l.label}
              </Link>
            ))}
            <Link href="/quote" className="btn btn-primary btn-nav mt-2">
              Request Quote
            </Link>
            <a href={PARTNER_PORTAL_URL} className="btn btn-navy btn-nav mt-2">
              Login/Register
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
